import { Card, Col, Row, Button, Table, Select, Dropdown, Menu, Input, Avatar, Tooltip, message, DatePicker, Typography, Tag, Drawer } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import React, { useEffect, useState, useCallback } from 'react';
import { formatDate, formatDateString, formatToTL, hasRole, normalizeTurkish } from '../../utils';
import ProposalService from '../../services/ProposalService';
import { useParams } from 'react-router-dom';
import CompanyService from '../../services/CompanyService';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';
import PaymentModal from '../ProjectFollowUp/PaymentModal';
import locale from 'antd/es/date-picker/locale/tr_TR';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const ProposalsList = () => {
    const [activeTab, setActiveTab] = useState(1);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [filteredProposals, setFilteredProposals] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState(0);

    const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
    const [invoiceDateRange, setInvoiceDateRange] = useState<[string | null, string | null]>([null, null]);
    const [paidDateRange, setPaidDateRange] = useState<[string | null, string | null]>([null, null]);
    const [planningPaidDateRange, setPlanningPaidDateRange] = useState<[string | null, string | null]>([null, null]);
    const [approvedPlanningDateRange, setApprovedPlanningDateRange] = useState<[string | null, string | null]>([null, null]);
    const [requestedPlanningDateRange, setRequestedPlanningDateRange] = useState<[string | null, string | null]>([null, null]);
    const [documentUploadedAtRange, setDocumentUploadedAtRange] = useState<[string | null, string | null]>([null, null]);

    const getFilterProposal = () => {
        setCurrentPage(0);
        setFilter(filter + 1);
        setOpen(false);
    };

    const onClose = () => {
        setOpen(false);
    };

    const showDrawer = () => {
        setOpen(true);
    };

    const filterClear = () => {
        setSearchText("");
        setDateRange([null, null]);
        setInvoiceDateRange([null, null]);
        setPaidDateRange([null, null]);
        setPlanningPaidDateRange([null, null]);
        setApprovedPlanningDateRange([null, null]);
        setRequestedPlanningDateRange([null, null]);
        setDocumentUploadedAtRange([null, null]);
        setCurrentPage(0);
        setFilter(filter + 1);
    };

    const [sortField, setSortField] = useState("invoiceDate");
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

    const { id } = useParams<{ id: string }>();
    const userId = id ?? localStorage.getItem('id');

    const onDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        if (dates) {
            setDateRange([
                dates[0]?.format('YYYY-MM-DD') ?? null,
                dates[1]?.format('YYYY-MM-DD') ?? null
            ]);
        } else {
            setDateRange([null, null]);
        }
    };

    const onInvoiceDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        if (dates) {
            setInvoiceDateRange([
                dates[0]?.format('YYYY-MM-DD') ?? null,
                dates[1]?.format('YYYY-MM-DD') ?? null
            ]);
        } else {
            setInvoiceDateRange([null, null]);
        }
    };

    const onPaidDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        if (dates) {
            setPaidDateRange([
                dates[0]?.format('YYYY-MM-DD') ?? null,
                dates[1]?.format('YYYY-MM-DD') ?? null
            ]);
        } else {
            setPaidDateRange([null, null]);
        }
    };

    const onPlanningPaidDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        if (dates) {
            setPlanningPaidDateRange([
                dates[0]?.format('YYYY-MM-DD') ?? null,
                dates[1]?.format('YYYY-MM-DD') ?? null
            ]);
        } else {
            setPlanningPaidDateRange([null, null]);
        }
    };

    const fetchProposals = useCallback(async (params: any) => {
        setLoading(true);
        try {
            params.statuIds = [4, 7].join(',');
            let response: any = await ProposalService.getMe(params);
            setFilteredProposals(response.data.content);
            setTotalItems(response.data.totalElements);
        } catch (error) {
            console.error('Teklif verileri alınırken hata oluştu:', error);
            message.error('Teklif verileri alınırken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounce edilmiş fetchProposals fonksiyonunu oluşturuyoruz
    const debouncedFetchProposals = useCallback(
        debounce((params: any) => {
            fetchProposals(params);
        }, 300), // 300ms debounce süresi
        [fetchProposals]
    );

    useEffect(() => {
        if (activeTab === 1) {
            setSortField("invoiceDate");
            setSortDirection("ASC");
        } else if (activeTab === 2) {
            setSortField("paidDate");
            setSortDirection("DESC");
        }
        filterClear();
    }, [activeTab])


    useEffect(() => {
        let isPaid;
        if (activeTab === 1) {
            isPaid = false;
        } else if (activeTab === 2) {
            isPaid = true;
        }

        const [start, end] = dateRange;
        const [invStart, invEnd] = invoiceDateRange;
        const [paidStart, paidEnd] = paidDateRange;
        const [planPaidStart, planPaidEnd] = planningPaidDateRange;
        const [approvedStart, approvedEnd] = approvedPlanningDateRange;
        const [requestedStart, requestedEnd] = requestedPlanningDateRange;
        const [docUploadStart, docUploadEnd] = documentUploadedAtRange;

        const params = {
            page: currentPage,
            size: pageSize,
            isPaid: isPaid, // activeTab'e bağlı olarak isPaid değerini ayarla
            company: normalizeTurkish(searchText), // Arama metnini ekle
            onlyPartner: false,
            startDate: dateRange[0], // Tarih aralığı başlangıcı
            endDate: dateRange[1],   // Tarih aralığı bitişi
            sort: sortField,
            direction: sortDirection,
            ...(start && end && { startDate: start, endDate: end }),
            ...(invStart && invEnd && { startInvoiceDate: invStart, endInvoiceDate: invEnd }),
            ...(paidStart && paidEnd && { startPaidDate: paidStart, endPaidDate: paidEnd }),
            ...(planPaidStart && planPaidEnd && { startPlanningPaidDate: planPaidStart, endPlanningPaidDate: planPaidEnd }),
            ...(approvedStart && approvedEnd && { startApprovedPlanningDate: approvedStart, endApprovedPlanningDate: approvedEnd }),
            ...(requestedStart && requestedEnd && { startRequestedPlanningDate: requestedStart, endRequestedPlanningDate: requestedEnd }),
            ...(docUploadStart && docUploadEnd && { startDocumentUploadedAt: docUploadStart, endDocumentUploadedAt: docUploadEnd }),
        };
        setFilteredProposals([]);
        debouncedFetchProposals(params);

        // Cleanup fonksiyonu ile debounce'i iptal ediyoruz
        return () => {
            debouncedFetchProposals.cancel();
        };
    }, [currentPage, pageSize, userId, searchText, sortField, sortDirection, debouncedFetchProposals, filter]);

    const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
        setSortField(sorter.field || 'createdAt');
        setSortDirection(sorter.order === 'ascend' ? 'ASC' : 'DESC');
    };

    const onDateChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
        if (dates) {
            setDateRange([
                dates[0]?.format('YYYY-MM-DD') ?? null,
                dates[1]?.format('YYYY-MM-DD') ?? null,
            ]);
        } else {
            setDateRange([null, null]);
        }
    };

    const columns: any = [
        {
            title: 'Firma Adı',
            dataIndex: ['company', 'name'],
            key: 'companyName',
            render: (text: string, record: any) => {
                const displayText = text?.length > 28 ? `${text.substring(0, 28)}...` : text;
                return (
                    <div className="d-flex align-items-center">
                        <div className='avatar-image-logo' style={{ marginRight: 10 }}>
                            <Avatar className='avatar-image-logo' style={{ backgroundColor: '#f0f0f0', color: '#000' }}>
                                {record?.company?.name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                        </div>
                        <Tooltip title={text?.toUpperCase()}>
                            <span>{displayText?.toUpperCase()}</span>
                        </Tooltip>
                    </div>
                )
            },
        },
        {
            title: 'Teklif No',
            dataIndex: 'proposalNumber',
            key: 'proposalNumber',
            sorter: true,
            render: (proposalNumber: string) => (
                <span style={{ color: '#4180f6' }}>
                    {proposalNumber}
                </span>
            ),
        },
        {
            title: 'Teklif Tarihi',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            render: (date: string) => formatDateString(date),
        },
        {
            title: 'Teklif Tutarı',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            sorter: true,
            width: 130,
            render: (price: any, record: any) => (
                <Text>{formatToTL(record.totalPrice)}</Text>
            ),
        },
        {
            title: 'Fatura Tutarı',
            dataIndex: 'invoiceAmount',
            key: 'invoiceAmount',
            sorter: true,
            width: 150,
            render: (price: any, record: any) => (
                <Text>{formatToTL(record.invoiceAmount)}</Text>
            ),
        },
        {
            title: 'Fatura Tarihi',
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            sorter: true,
            render: (date: string) => formatDateString(date),
        },
        {
            title: (activeTab === 1) ? "Ödenecek Tutar" : 'Ödenen Tutar',
            dataIndex: 'incomeSharePrice',
            key: 'incomeSharePrice',
            sorter: true,
            render: (incomeSharePrice: number) => `${formatToTL(incomeSharePrice)}`,
        },
        {
            title: 'Ödeme Planı',
            dataIndex: 'invoiceDate',
            key: 'invoiceDateStatus',
            sorter: true,
            render: (invoiceDate: string) => {
                if (!invoiceDate) {
                    return <Text>-</Text>;
                }
                const today = dayjs();
                const adjustedDueDate = dayjs(invoiceDate).add(31, 'days');

                return (<>
                    {adjustedDueDate.format('DD/MM/YYYY')}
                </>);
            },
        },
        ...(activeTab === 2 ? [
            {
                title: 'Ödeme Tarihi',
                dataIndex: 'paidDate',
                key: 'paidDate',
                sorter: true,
                render: (paidDate: string) => formatDateString(paidDate),
            },
        ] : []),
        {
            title: '',
            key: 'actions',
            align: 'right' as const,
            render: (text: any, record: any) => (
                <Button type='default' className='d-button action-button width-auto' target="_blank" href={record?.financialInfoUrl}>
                    Fatura Görüntüle
                </Button>
            ),
        },
    ];

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    return (
        <>
            <Drawer
                title="Filtre"
                closable={{ 'aria-label': 'Close Button' }}
                onClose={onClose}
                open={open}
            >
                <Row gutter={[16, 16]}>
                    {(activeTab === 2) &&
                        <Col span={24}>
                            <label>Ödeme Tarihi</label>
                            <RangePicker
                                format="DD/MM/YYYY"
                                locale={locale}
                                className="d-input-filter mr-8px"
                                value={[
                                    paidDateRange[0] ? dayjs(paidDateRange[0]) : null,
                                    paidDateRange[1] ? dayjs(paidDateRange[1]) : null,
                                ]}
                                onChange={onPaidDateRangeChange}
                            />
                        </Col>
                    }
                    <Col span={24}>
                        <label>Ödeme Planı</label>
                        <RangePicker
                            format="DD/MM/YYYY"
                            locale={locale}
                            className="d-input-filter mr-8px"
                            value={[
                                planningPaidDateRange[0] ? dayjs(planningPaidDateRange[0]) : null,
                                planningPaidDateRange[1] ? dayjs(planningPaidDateRange[1]) : null,
                            ]}
                            onChange={onPlanningPaidDateRangeChange}
                        />
                    </Col>
                    <Col span={24}>
                        <label>Fatura Tarihi</label>
                        <RangePicker
                            format="DD/MM/YYYY"
                            locale={locale}
                            className="d-input-filter mr-8px"
                            value={[
                                invoiceDateRange[0] ? dayjs(invoiceDateRange[0]) : null,
                                invoiceDateRange[1] ? dayjs(invoiceDateRange[1]) : null,
                            ]}
                            onChange={onInvoiceDateRangeChange}
                        />
                    </Col>
                    <Col span={24}>
                        <label>Teklif Tarihi</label>
                        <RangePicker
                            format="DD/MM/YYYY"
                            locale={locale}
                            className="d-input-filter mr-8px d-input-filter"
                            style={{ marginRight: 10 }}
                            value={[
                                dateRange[0] ? dayjs(dateRange[0]) : null,
                                dateRange[1] ? dayjs(dateRange[1]) : null,
                            ]}
                            onChange={onDateRangeChange}
                        />
                    </Col>
                </Row>
                <Row gutter={[16, 16]} className="justify-content-end mt-16px">
                    <Col>
                        <Button
                            type="default"
                            onClick={() => filterClear()}
                        >
                            Temizle
                        </Button>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={() => getFilterProposal()}>
                            Filtrele
                        </Button>
                    </Col>
                </Row>
            </Drawer >
            <Row gutter={16} style={{ marginTop: '24px' }}>
                <Col span={24}>
                    <Row justify="space-between" style={{ marginBottom: '16px' }} gutter={[16, 16]}>
                        <div>
                            <Button
                                type={activeTab === 1 ? 'primary' : 'default'}
                                onClick={() => {
                                    setActiveTab(1);
                                    setCurrentPage(0); // Sayfayı sıfırla
                                    setPageSize(10);   // Sayfa boyutunu sıfırla (isteğe bağlı)
                                }}
                                style={{ marginRight: '8px' }}
                                className='d-button tab-button width-auto'
                            >
                                Bekleyen Ödemeler
                            </Button>
                            <Button
                                type={activeTab === 2 ? 'primary' : 'default'}
                                onClick={() => {
                                    setActiveTab(2);
                                    setCurrentPage(0); // Sayfayı sıfırla
                                    setPageSize(10);   // Sayfa boyutunu sıfırla (isteğe bağlı)
                                }}
                                className='d-button tab-button width-auto'
                            >
                                Alınan Ödemeler
                            </Button>
                        </div>

                    </Row>
                    <Card
                        className="br-30px mb-120px proposal-card"
                        bordered={false}
                        style={{ marginTop: '24px' }}
                    >
                        <Row gutter={[16, 16]} justify="space-between" align="middle">
                            <Col>
                                <Row align="bottom">
                                    <Title level={4} style={{ margin: 0 }}>
                                        {activeTab === 1 ? "Ödemesi Bekleyen Projeler" : "Ödemesi Tamamlanan Projeler"}
                                    </Title>
                                    <Text type="secondary" className="ms-5" ><strong>({totalItems})</strong> <span style={{ fontWeight: "300" }}>kayıt listelendi</span></Text>
                                </Row>
                            </Col>
                            <Col>
                                <Row gutter={[16, 16]}>
                                    <Col>
                                        <Input
                                            allowClear
                                            placeholder="Ara"
                                            prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                                            className="d-input d-input-form d-input-filter"
                                            value={searchText}
                                            onChange={handleSearchChange}
                                            style={{ width: 200 }}
                                        />
                                    </Col>
                                    <Col>
                                        <Button type="primary" onClick={showDrawer}>
                                            Filtrele
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Table
                            scroll={{ x: 768 }}
                            columns={columns}
                            dataSource={filteredProposals}
                            rowKey="proposalNumber"
                            loading={loading}
                            pagination={{
                                current: currentPage + 1,
                                pageSize: pageSize,
                                total: totalItems,
                                onChange: (page, pageSize) => {
                                    setCurrentPage(page - 1);
                                    setPageSize(pageSize || 10);
                                },
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '30'],
                            }}
                            style={{ marginTop: '24px' }}
                            onChange={handleTableChange}
                        />
                    </Card>
                </Col>
            </Row>

        </>

    );
};

export default ProposalsList;
